<script lang="ts">
	import MetadataList from '#lib/MetadataList.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import type { I18nKey } from '#lib/i18n/translations.ts';
	import type { PersonSource, PoliticianSidejob } from '#src/types/sources.ts';

	interface Props {
		source: PersonSource;
	}
	let { source }: Props = $props();

	const mandateWonLabels: Record<string, I18nKey> = {
		constituency: 'sources.person.mandate.constituency',
		list: 'sources.person.mandate.list',
		moved_up: 'sources.person.mandate.movedUp'
	};

	const committeeRoleLabels: Record<string, I18nKey> = {
		member: 'sources.person.committeeRole.member',
		alternate_member: 'sources.person.committeeRole.alternateMember',
		chairperson: 'sources.person.committeeRole.chairperson',
		vice_chairperson: 'sources.person.committeeRole.viceChairperson'
	};

	function committeeRoleLabel(role: string): string {
		const key = committeeRoleLabels[role];
		return key ? t(key) : role;
	}

	function sidejobIncome(job: PoliticianSidejob): string | null {
		if (job.income_eur != null) {
			return `€${job.income_eur.toLocaleString()}`;
		}
		if (job.income_level) {
			return t('sources.person.incomeLevel', { level: job.income_level });
		}
		return null;
	}

	const coreMetadata = $derived.by(() => {
		const m: Record<string, unknown> = {};
		if (source.party) {
			m[t('sources.person.party')] = source.party;
		}
		if (source.fraction) {
			m[t('sources.person.fraction')] = source.fraction;
		}
		if (source.parliament_period) {
			m[t('sources.person.parliament')] = source.parliament_period;
		}
		if (source.constituency) {
			m[t('sources.person.constituency')] = source.constituency;
		}
		if (source.mandate_won) {
			const key = mandateWonLabels[source.mandate_won];
			m[t('sources.person.mandateWon')] = key ? t(key) : source.mandate_won;
		}
		if (source.year_of_birth) {
			m[t('sources.person.born')] = source.year_of_birth;
		}
		if (source.occupation) {
			m[t('sources.person.occupation')] = source.occupation;
		}
		if (source.education) {
			m[t('sources.person.education')] = source.education;
		}
		if (source.residence) {
			m[t('sources.person.residence')] = source.residence;
		}
		return m;
	});

	const lobbyMetadata = $derived.by(() => {
		const m: Record<string, unknown> = {};
		if (source.register_number) {
			m[t('sources.person.registerNumber')] = source.register_number;
		}
		if (source.activity_type) {
			m[t('sources.person.activityType')] = source.activity_type;
		}
		if (source.financial_expenses_eur) {
			m[t('sources.person.financialExpenses')] =
				`€${source.financial_expenses_eur.from.toLocaleString()}–${source.financial_expenses_eur.to.toLocaleString()}`;
		}
		return m;
	});

	const hasLobbyData = $derived(
		source.register_number != null ||
			source.fields_of_interest?.length ||
			source.lobby_methods?.length ||
			source.memberships?.length
	);
</script>

{#if Object.keys(coreMetadata).length}
	<MetadataList metadata={coreMetadata} />
{/if}

{#if source.sidejobs?.length || source.committees?.length}
	{#if source.sidejobs?.length}
		<div class="section">
			<h4>{t('sources.person.sidejobs')}</h4>
			<ul class="compact-list">
				{#each source.sidejobs as job (job)}
					{@const income = sidejobIncome(job)}
					<li>
						{job.label}{#if job.organization}&nbsp;— {job.organization}{/if}
						{#if income}
							<span class="detail-note">({income})</span>
						{/if}
					</li>
				{/each}
			</ul>
		</div>
	{/if}

	{#if source.committees?.length}
		<div class="section">
			<h4>{t('sources.person.committees')}</h4>
			<ul class="compact-list">
				{#each source.committees as committee (committee)}
					<li>
						{committee.name}{#if committee.role}&nbsp;— {committeeRoleLabel(committee.role)}{/if}
						{#if committee.period}
							<span class="detail-note">({committee.period})</span>
						{/if}
					</li>
				{/each}
			</ul>
		</div>
	{/if}
{/if}

{#if hasLobbyData}
	{#if Object.keys(lobbyMetadata).length}
		<MetadataList metadata={lobbyMetadata} />
	{/if}

	{#if source.fields_of_interest?.length}
		<div class="section">
			<h4>{t('sources.person.fieldsOfInterest')}</h4>
			<div class="tags">
				{#each source.fields_of_interest as field (field)}
					<span class="tag">{field}</span>
				{/each}
			</div>
		</div>
	{/if}
	{#if source.memberships?.length}
		<div class="section">
			<h4>{t('sources.company.memberships')}</h4>
			<ul class="compact-list">
				{#each source.memberships as m (m)}
					<li>{m}</li>
				{/each}
			</ul>
		</div>
	{/if}
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.section {
		@include lib.flex-column();
		gap: 0.5rem;
		.tags {
			@include lib.flex-row();
			flex-wrap: wrap;
			gap: 0.25rem;

			.tag {
				font-size: 0.75rem;
				padding: 0.125rem 0.5rem;
				border-radius: 999px;
				background: var(--surface-3);
			}
		}

		.compact-list {
			margin: 0;
			padding-left: 1rem;
			font-size: 0.875rem;

			li {
				margin-bottom: 0.125rem;

				.detail-note {
					color: var(--text-secondary);
				}
			}
		}
	}
</style>
